/* eslint-disable react/prop-types */
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText
} from '@material-ui/core'
import { useTheme, ThemeProvider } from '@material-ui/core/styles'
import Form from '@rjsf/material-ui'
import { isNil, isEmpty } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { fetchUser } from 'api'
import { AutoComplete } from 'app/components/generic/FormFields'
import { navigationPaths } from 'app/config'
import { composeBatchDetails } from 'app/pages/Class/Inventory/transformers'

import * as Styles from './styles'

import { BookingTypes } from '../../../../../../constants'
import {
  demoUiSchema,
  classUiSchema,
  getTransformErrors
} from '../../formConfig'
import * as styles from '../CheckoutDetails/styles'

const fetchParentDetails = async (searchedText) => {
  try {
    if (!isEmpty(searchedText) && searchedText.length === 10) {
      const queries = {
        phone_number: searchedText
      }

      const response = await fetchUser({ queries }, 'parent')

      if (response.results.length === 1) {
        return response.results.map((item) => {
          const {
            uuid,
            first_name,
            last_name,
            phone_number,
            email_address,
            state,
            coin_summary,
            member_status,
            credits
          } = item?.user

          return {
            uuid,
            value: uuid,
            name: `${phone_number} (${first_name} ${last_name})`,
            first_name,
            last_name,
            phone_number,
            email: email_address,
            state,
            students: item?.students,
            coins: coin_summary?.remaining_coins,
            credits,
            member_status
          }
        })
      } else if (response.results.length > 1) {
        throw new Error('Multiple user found with same number')
      } else {
        return []
      }
    }
  } catch (error) {
    console.log('Error in fetching user :- ', error)
    throw error
  }
}

const AddNewUser = ({ user, onClick, disabled = false }) => {
  return (
    <Styles.NoUser>
      Can't find?{' '}
      <Styles.Button onClick={onClick} disabled={disabled}>
        <span>Add new {user}</span>
      </Styles.Button>
    </Styles.NoUser>
  )
}

const InputForm = React.forwardRef(
  (
    {
      bookingType,
      schema,
      errorSchema,
      formData,
      onChangeForm,
      onSubmit,
      selectedParentDetails,
      selectedStudentDetails,
      handleParentSelection,
      handleStudentSelection,
      students,
      inputError,
      setSelectedParentDetails,
      verifyDetails,
      allowAddingNewUser,
      selectedBatch
    },
    ref
  ) => {
    console.log('InputForm', selectedParentDetails)
    const theme = useTheme()

    const modifiedTheme = {
      ...theme,
      props: {
        ...theme.props,
        MuiTextField: {
          ...theme.props.MuiTextField,
          variant: 'outlined',
          size: 'small',
          className: 'inventory-input'
        },
        MuiCheckbox: {
          color: 'primary',
          size: 'small',
          className: 'inventory-checkbox'
        }
      }
    }
    const { batchErrorType, isSameSlotBooked } =
      composeBatchDetails(selectedBatch)

    const uiSchema =
      bookingType === BookingTypes.DEMO ||
      bookingType === BookingTypes.MEMBERSHIP ||
      bookingType === BookingTypes.EXPLORATION ||
      bookingType === BookingTypes.EXCEL
        ? demoUiSchema
        : bookingType === BookingTypes.CLASS
        ? classUiSchema
        : null

    const cities = useSelector((state) => state.commonReducer.cities)
    const [clearValue, setClearValue] = useState(false)

    const getState = () => {
      const centre = JSON.parse(localStorage.getItem('SELECTED_CENTRE'))
      const city = centre?.city
      const state = cities.data.find((el) => el.name === city)?.state?.code
      return state
    }

    const onClickAddParent = () => {
      const centre = JSON.parse(localStorage.getItem('SELECTED_CENTRE'))
      const state = getState()
      window.open(
        `${window.location.origin}${navigationPaths.parent}?openModalForm=1&centre=${centre?.value}&state=${state}`
      )
    }

    const onClickAddStudent = () => {
      const state = getState()
      const parentId = selectedParentDetails?.uuid
      setSelectedParentDetails({})
      setClearValue(true)
      window.open(
        `${window.location.origin}${
          navigationPaths.student
        }?openModalForm=1&parent=${parentId}&no_phone_number=${true}&state=${state}`
      )
    }

    useEffect(() => {
      setClearValue(isEmpty(selectedParentDetails))
    }, [selectedParentDetails])

    const onChange = (name, values) => {
      console.log('onChange', name, values)
      handleParentSelection(name, values)
    }

    return (
      <Styles.FormContainer>
        <ThemeProvider theme={modifiedTheme}>
          <FormControl
            fullWidth
            variant="outlined"
            size="small"
            className="parent-selection"
          >
            <AutoComplete
              label="Parent Number"
              placeholder="Enter Parent Mobile Number"
              minCharactersToSearch={10}
              required
              async
              fetchOnMount={false}
              searchWithEmptyValue={false}
              clearOnBlur={false}
              fetchValues={fetchParentDetails}
              onChange={onChange}
              emptyValue={clearValue}
              {...(!isEmpty(selectedParentDetails)
                ? { defaultValue: selectedParentDetails }
                : null)}
            />

            {inputError.parent && (
              <FormHelperText error>{inputError.parent}</FormHelperText>
            )}
            {!allowAddingNewUser ? (
              <AddNewUser user="parent" onClick={onClickAddParent} />
            ) : null}
          </FormControl>

          <FormControl
            fullWidth
            variant="outlined"
            size="small"
            className="student-selection"
            required
          >
            <Styles.Wrapper>
              <InputLabel id="student-select">Student</InputLabel>
              <Select
                label="Student"
                labelId="student-select"
                value={
                  !isEmpty(selectedStudentDetails)
                    ? selectedStudentDetails?.user?.uuid
                    : ''
                }
                onChange={handleStudentSelection}
                disabled={isEmpty(selectedParentDetails)}
              >
                {students.map((item) => (
                  <MenuItem
                    key={item.user.uuid}
                    value={item.user.uuid}
                    disabled={item.isDisabled}
                  >
                    {`${item.user.full_name} (${`${
                      item.standard?.name ? item.standard.name : ''
                    }`})`}
                    {` ${item?.message ? item?.message : ''}`}
                  </MenuItem>
                ))}
              </Select>
            </Styles.Wrapper>

            {inputError.student && (
              <FormHelperText error>{inputError.student}</FormHelperText>
            )}
            {!allowAddingNewUser ? (
              <AddNewUser
                user="student"
                onClick={onClickAddStudent}
                disabled={isEmpty(selectedParentDetails)}
              />
            ) : null}
            {batchErrorType === 'session_conflict' ? (
              isSameSlotBooked ? (
                <Styles.ErrorBox>
                  <strong>Session Conflict: </strong> The child has the same
                  class already booked. <br />
                </Styles.ErrorBox>
              ) : (
                <Styles.ErrorBox>
                  <strong>Timing Conflict: </strong> The child has another class
                  during the same time. <br />
                  You can still book the session after informing the parent.
                </Styles.ErrorBox>
              )
            ) : null}
          </FormControl>

          {/* {selectedStudentDetails?.standard?.name && (
            <styles.KeyValue>
              <styles.Key>Student Grade:</styles.Key>
              <styles.Value>
                {selectedStudentDetails?.standard?.name}
              </styles.Value>
            </styles.KeyValue>
          )} */}

          {!isNil(schema) ? (
            <Form
              ref={ref}
              schema={schema}
              uiSchema={uiSchema}
              onSubmit={onSubmit}
              transformErrors={getTransformErrors(schema)}
              noHtml5Validate
              showErrorList={false}
              onChange={onChangeForm}
              formData={formData}
              extraErrors={errorSchema}
            >
              <React.Fragment />
            </Form>
          ) : null}
        </ThemeProvider>
      </Styles.FormContainer>
    )
  }
)

export { InputForm }
